import React, { FC, useState, useMemo } from "react";
import { Typography, Table, Space, Tooltip, Select, Progress } from "antd";
import { WarningOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { TeamMembership, TeamMembershipIds, PricingType } from "../../data-types";
import { FeeEarnerAndRatesCascaderContainer } from "~/containers/fee-earner-and-rates-cascader-container";
import { GET_TOP_DOWN_RATES } from "~/graphql/query";
import { useQuery } from "@apollo/client";
import styles from "./deprecated-rates-modal.module.css";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  "warning-icon": warningIconClassName,
  "danger-icon": dangerIconClassName,
  "new-rate-select": newRateSelectClassName,
  table: tableClassName,
  "progress-container": progressContainerClassName,
  "progress-text-container": progressTextContainerClassName,
  "progress-message": progressMessageClassName,
  "progress-status-icon": progressStatusIconClassName,
} = styles;

export type DeprecatedRatesContentProps = {
  deprecatedRates: TeamMembership[];
  matterId: number;
  clientId?: number;
  externalMatterId?: number | null;
  organisationalUnitId?: number;
  currency: { symbol: string; code: string };
  pricingType: PricingType;
  onReplaceTeamMember: (data: {
    teamMemberToAdd: TeamMembershipIds & { feeEarnerLevelId: number | null };
    teamMemberToRemove: TeamMembershipIds;
  }) => void;
  tableHeight?: number;
  replacedRateIds?: number[];
};

export const DeprecatedRatesContent: FC<DeprecatedRatesContentProps> = ({
  deprecatedRates,
  matterId,
  clientId,
  externalMatterId,
  organisationalUnitId,
  currency,
  pricingType,
  onReplaceTeamMember,
  tableHeight = 300,
  replacedRateIds = [],
}) => {
  const FE_LABEL = VPDLabel("FeeEarner");
  const FE_LEVEL_LABEL = VPDLabel("FeeEarnerLevel");

  const [rowSelections, setRowSelections] = useState<
    Record<
      number,
      {
        feeEarnerLevelId: number | null;
        feeEarnerId: number | null;
        selectedRateId: number | null;
      }
    >
  >({});

  const [selectedRates, setSelectedRates] = useState<Record<number, number | null>>({});

  const { data: ratesData = undefined, loading: loadingRates } = useQuery(GET_TOP_DOWN_RATES, {
    variables: {
      filter: {
        clientId,
        organisationalUnitId,
        currencyCode: currency.code,
        matterId,
      },
    },
  });

  const topDownRateOptions = ratesData?.getTopDownRates || [];

  const feeEarnerCascadeRender = (values: any, record: TeamMembership) => {
    const rowId = record.id;
    const currentRowSelection = rowSelections[rowId] || {
      feeEarnerLevelId: null,
      feeEarnerId: null,
      selectedRateId: null,
    };

    return (
      <FeeEarnerAndRatesCascaderContainer
        matterId={matterId}
        clientId={clientId}
        externalMatterId={externalMatterId}
        organisationalUnitId={organisationalUnitId}
        selectedFeeEarnerLevelId={currentRowSelection.feeEarnerLevelId}
        selectedFeeEarnerId={currentRowSelection.feeEarnerId}
        selectedRateId={currentRowSelection.selectedRateId}
        onChange={(values: {
          feeEarnerId: number | null;
          selectedRateId: number;
          feeEarnerLevelId: number | null;
        }) => {
          setRowSelections((prev) => ({
            ...prev,
            [rowId]: {
              feeEarnerLevelId: values.feeEarnerLevelId,
              feeEarnerId: values.feeEarnerId,
              selectedRateId: values.selectedRateId,
            },
          }));

          if (values.selectedRateId) {
            onReplaceTeamMember({
              teamMemberToAdd: {
                feeEarnerId: values.feeEarnerId,
                feeEarnerLevelId: values.feeEarnerLevelId,
                rateId: values.selectedRateId,
              },
              teamMemberToRemove: {
                feeEarnerId: record.feeEarnerId,
                rateId: record.rateId,
              },
            });
          }
        }}
        onClear={() => {
          setRowSelections((prev) => {
            const updated = { ...prev };
            delete updated[rowId];
            return updated;
          });
        }}
        currency={currency}
      />
    );
  };

  const tableColumns: ColumnType<TeamMembership>[] = useMemo(
    () => [
      {
        title: FE_LABEL,
        dataIndex: ["feeEarner", "name"],
        key: "feeEarner.name",
        render: (name: string, record: TeamMembership) =>
          name ? (
            record.feeEarner?.deprecated ? (
              <Space>
                {name}
                <Tooltip title={`${FE_LABEL} is deprecated`}>
                  <WarningOutlined className={dangerIconClassName} />
                </Tooltip>
              </Space>
            ) : (
              name
            )
          ) : (
            "N/A"
          ),
      },
      {
        title: FE_LEVEL_LABEL,
        dataIndex: ["feeEarner", "feeEarnerLevel", "description"],
        key: "feeEarner.feeEarnerLevel.description",
        render: (description: string, record: TeamMembership) => {
          return description || record.rate.feeEarnerLevel?.description || "N/A";
        },
      },
      {
        title: "Amount",
        dataIndex: ["rate", "amount"],
        key: "amount",
        render: (amount: number) => `${currency.symbol}${amount}`,
      },
      {
        title: "Rate description",
        dataIndex: ["rate", "description"],
        key: "description",
        render: (description: string, record: TeamMembership) =>
          record.rate.deprecated ? (
            <Space>
              {description || "N/A"}
              <Tooltip title="Rate is deprecated">
                <WarningOutlined className={dangerIconClassName} />
              </Tooltip>
            </Space>
          ) : (
            description || "N/A"
          ),
      },
      {
        title: "New Rate",
        dataIndex: [],
        key: "newRate",
        width: "50%",
        render: (record: TeamMembership) => {
          if (pricingType === PricingType.TopDown) {
            const rateGroup = topDownRateOptions.find(
              (group) => group.feeEarnerLevelId === record.rate.feeEarnerLevel?.id
            );
            const options = rateGroup ? rateGroup.rates : [];
            return (
              <Select
                className={newRateSelectClassName}
                value={selectedRates[record.id] ?? undefined}
                onChange={(value) => {
                  const newRateId = value as number;
                  setSelectedRates((prev) => ({ ...prev, [record.id]: newRateId }));
                  onReplaceTeamMember({
                    teamMemberToAdd: {
                      feeEarnerId: null,
                      feeEarnerLevelId: null,
                      rateId: newRateId,
                    },
                    teamMemberToRemove: { feeEarnerId: null, rateId: record.rateId },
                  });
                }}
                placeholder="Select a new rate"
              >
                {options.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {`${currency.symbol}${option.amount} ${option.description}`}
                  </Select.Option>
                ))}
              </Select>
            );
          }
          return feeEarnerCascadeRender({}, record);
        },
      },
    ],
    [pricingType, topDownRateOptions, currency, onReplaceTeamMember, selectedRates, rowSelections]
  );

  const replacedCount = replacedRateIds.length;
  const totalCount = deprecatedRates.length;
  const progressPercentage = totalCount > 0 ? Math.round((replacedCount / totalCount) * 100) : 0;
  const allReplaced = totalCount > 0 && replacedCount === totalCount;

  return (
    <>
      {deprecatedRates.length > 0 ? (
        <>
          {/* Progress indicator */}
          <div className={progressContainerClassName}>
            <Space className={progressMessageClassName} size="middle">
              {allReplaced ? (
                <CheckCircleOutlined
                  className={progressStatusIconClassName}
                  style={{ color: "#52c41a" }}
                />
              ) : (
                <WarningOutlined
                  className={progressStatusIconClassName}
                  style={{ color: "#faad14" }}
                />
              )}
              <Typography.Text strong>
                {allReplaced
                  ? "All rates have been successfully replaced."
                  : `${replacedCount} of ${totalCount} rates replaced. ${
                      totalCount - replacedCount
                    } rates still need replacement.`}
              </Typography.Text>
            </Space>
            <div className={progressTextContainerClassName}>
              <Typography.Text>Replacement Progress</Typography.Text>
            </div>
            <Progress
              percent={progressPercentage}
              status={allReplaced ? "success" : "active"}
              strokeColor={allReplaced ? "#52c41a" : "#1890ff"}
            />
          </div>

          <Table
            dataSource={deprecatedRates}
            columns={tableColumns}
            className={tableClassName}
            pagination={false}
            scroll={{ y: tableHeight }}
            rowKey="id"
          />
        </>
      ) : (
        <Typography.Text>
          <Space>
            <WarningOutlined className={warningIconClassName} />
            Deprecated rates have been replaced. Please double-check all matter pricing details.
          </Space>
        </Typography.Text>
      )}
    </>
  );
};
